import { VisualizationTable } from '../use-visualization-store/useVisualizationStore';
import { generateCodeBlock } from './generateCodeBlock';
import { htmlNodes } from './htmlNodes';
import { patterns } from './regex';
import { replaceVisualizationLinks } from './replaceVisualizationLinks';

interface MarkdownParserArgs {
  content: string;
  charts: Record<string, HTMLDivElement>;
  tables: Record<string, VisualizationTable>;
}

const isSeparatorRow = (line: string): boolean => {
  return /^[\|\-:\s]+$/.test(line);
};

const parseMarkdownTable = (tableMarkdown: string): string => {
  const lines = tableMarkdown
    .split('\n')
    .map(line => line.trim())
    .filter(line => line);

  if (lines.length < 2) {
    return tableMarkdown;
  }

  const headerLine = lines[0];

  const hasSeparator = lines.length > 1 && isSeparatorRow(lines[1]);
  const dataLines = hasSeparator ? lines.slice(2) : lines.slice(1);

  const headerCells = headerLine
    .split('|')
    .map(cell => cell.trim())
    .filter(cell => cell);

  const headerRow = htmlNodes.tr(
    headerCells.map(cell => htmlNodes.th(cell)).join('')
  );

  const dataRows = dataLines
    .map(line => {
      const cells = line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell);

      return htmlNodes.tr(cells.map(cell => htmlNodes.td(cell)).join(''));
    })
    .join('');

  return htmlNodes.table(headerRow + dataRows);
};

export const markdownParser = async ({
  content,
  charts,
  tables,
}: MarkdownParserArgs) => {
  let result = content;

  result = generateCodeBlock(result);

  result = await replaceVisualizationLinks({
    originalContent: result,
    charts,
    tables,
  });

  result = result
    .replace(patterns.h1, (_, text) => htmlNodes.h1(text))
    .replace(patterns.h2, (_, text) => htmlNodes.h2(text))
    .replace(patterns.h3, (_, text) => htmlNodes.h3(text))
    .replace(patterns.h4, (_, text) => htmlNodes.h4(text))
    .replace(patterns.h5, (_, text) => htmlNodes.h5(text))
    .replace(patterns.unorderedList, match => {
      const items = match.replace(patterns.unorderedListItem, (_, text) =>
        htmlNodes.unorderedListItem(text)
      );
      return htmlNodes.unorderedList(items);
    })
    .replace(patterns.orderedList, match => {
      const items = match.replace(patterns.orderedListItem, (_, text) =>
        htmlNodes.orderedListItem(text)
      );
      return htmlNodes.orderedList(items);
    })
    .replace(patterns.code, (_, text) => htmlNodes.code(text))
    .replace(patterns.strong, (_, text) => htmlNodes.strong(text))
    .replace(patterns.em, (_, text) => htmlNodes.em(text))
    .replace(patterns.validUrl, url => htmlNodes.a(url, url))
    .replace(patterns.citationLink, (_, text) => text)
    .replace(patterns.link, (_, text, href) => htmlNodes.a(text, href))
    .replace(patterns.table, match => parseMarkdownTable(match))
    .replace(patterns.blankLine, '<br />');

  return result;
};
